// components/KnowledgeDetailModal.js
import KnowledgeInteractions from './KnowledgeInteractions'
import KnowledgeHistory from './KnowledgeHistory'
import ImageGallery from './ImageGallery'
import SolutionWithImageReferences from './SolutionWithImageReferences'
import ExportSettingsModal from './ExportSettingsModal'
import LoadingSpinner from './LoadingSpinner'
import './KnowledgeDetailModal.css'
import { useEffect, useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import UserService from '../services/UserService'
import toast from 'react-hot-toast'

function KnowledgeDetailModal ({
  item,
  onClose,
  currentUser,
  getCategoryIcon,
  getCategoryLabel,
  getDifficultyColor,
  getDifficultyLabel,
  userRole
}) {
  const [isExporting, setIsExporting] = useState(false)
  const [modalHeight, setModalHeight] = useState(null)
  const [showExportSettings, setShowExportSettings] = useState(false)

  if (!item) return null

  // Berechne die tatsächliche Viewport-Höhe für Mobile
  useEffect(() => {
    const updateModalHeight = () => {
      if (window.innerWidth <= 768) {
        // Verwende die kleinste verfügbare Viewport-Höhe mit einem kleinen Puffer
        const vh = window.innerHeight
        const svh = window.visualViewport?.height || vh
        const actualHeight = Math.min(vh, svh)
        // Reduziere um 2-4px als Puffer für Browser-UI
        const buffer = 4
        setModalHeight(`${Math.max(actualHeight - buffer, actualHeight * 0.98)}px`)
      } else {
        setModalHeight(null)
      }
    }

    // Initiale Berechnung
    updateModalHeight()
    
    // Event Listener für verschiedene Viewport-Änderungen
    window.addEventListener('resize', updateModalHeight)
    window.addEventListener('orientationchange', updateModalHeight)
    
    // Für Browser mit visualViewport API (bessere Unterstützung für Browser-UI)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateModalHeight)
      window.visualViewport.addEventListener('scroll', updateModalHeight)
    }

    // Timeout für verzögerte Berechnung nach Orientation Change
    let resizeTimeout
    const delayedUpdate = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(updateModalHeight, 100)
    }
    window.addEventListener('orientationchange', delayedUpdate)

    return () => {
      window.removeEventListener('resize', updateModalHeight)
      window.removeEventListener('orientationchange', updateModalHeight)
      window.removeEventListener('orientationchange', delayedUpdate)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateModalHeight)
        window.visualViewport.removeEventListener('scroll', updateModalHeight)
      }
      clearTimeout(resizeTimeout)
    }
  }, [])

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalStyle
    }
  }, [])

  const handleExport = () => {
    if (isExporting) return
    setShowExportSettings(true)
  }

  const performExport = async (targetLanguage = 'de') => {
    if (isExporting) return

    setIsExporting(true)
    const loadingToast = toast.loading('Exportiere Wissensbeitrag...')

    // Timeout-Handling (3 Minuten)
    const timeoutId = setTimeout(() => {
      toast.error('Export dauert länger als erwartet. Bitte warten Sie...', {
        id: loadingToast,
        duration: 5000
      })
    }, 180000) // 3 Minuten

    try {
      // Hole companyId
      if (!currentUser) {
        throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
      }

      const companyId = await UserService.getUserCompanyId(currentUser.uid)

      if (!companyId) {
        throw new Error('Company-ID konnte nicht ermittelt werden. Bitte versuchen Sie es erneut.')
      }

      // Rufe Cloud Function auf
      const functions = getFunctions(app, 'us-central1')
      const exportKnowledge = httpsCallable(functions, 'exportKnowledgeToPDF')

      const result = await exportKnowledge({
        knowledgeId: item.id,
        companyId: companyId,
        targetLanguage: targetLanguage
      })

      clearTimeout(timeoutId)

      // Prüfe ob result.data existiert
      if (!result || !result.data) {
        throw new Error('Keine Antwort von der Cloud Function erhalten. Bitte versuchen Sie es erneut.')
      }

      if (result.data.success) {
        // Validierung der ZIP-Größe vor Download (max 50MB)
        const maxZipSize = 50 * 1024 * 1024 // 50MB
        if (result.data.stats?.zipSize && result.data.stats.zipSize > maxZipSize) {
          throw new Error(
            `ZIP-Datei ist zu groß (${(result.data.stats.zipSize / 1024 / 1024).toFixed(2)}MB). ` +
            'Bitte reduzieren Sie die Anzahl der Bilder und versuchen Sie es erneut.'
          )
        }

        // Konvertiere Base64 zurück zu Blob
        const zipBase64 = result.data.zipBase64
        if (!zipBase64 || zipBase64.length === 0) {
          throw new Error('Export-Daten sind leer. Bitte versuchen Sie es erneut.')
        }

        try {
          const zipBinary = atob(zipBase64)
          const zipBytes = new Uint8Array(zipBinary.length)
          for (let i = 0; i < zipBinary.length; i++) {
            zipBytes[i] = zipBinary.charCodeAt(i)
          }
          const zipBlob = new Blob([zipBytes], { type: 'application/zip' })

          // Validierung: Prüfe ob Blob gültig ist
          if (zipBlob.size === 0) {
            throw new Error('Export-Datei ist leer. Bitte versuchen Sie es erneut.')
          }

          // Download auslösen
          const filename = result.data.filename || `${item.problemTitle || 'wissensbeitrag'}.zip`
          const url = window.URL.createObjectURL(zipBlob)
          const link = document.createElement('a')
          link.href = url
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          // Erfolgsmeldung mit Statistiken
          const stats = result.data.stats
          let successMessage = 'Wissensbeitrag erfolgreich exportiert!'
          if (stats) {
            if (stats.successCount > 0) {
              successMessage += ` (${stats.successCount} ${stats.successCount === 1 ? 'Bild' : 'Bilder'}`
              if (stats.failCount > 0) {
                successMessage += `, ${stats.failCount} fehlgeschlagen`
              }
              successMessage += ')'
            }
          }

          toast.success(successMessage, { id: loadingToast, duration: 5000 })
        } catch (blobError) {
          throw new Error(`Fehler beim Verarbeiten der Export-Datei: ${blobError.message}`)
        }
      } else {
        throw new Error('Export fehlgeschlagen. Bitte versuchen Sie es erneut.')
      }
    } catch (error) {
      clearTimeout(timeoutId)
      console.error('Export-Fehler:', error)
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        stack: error.stack
      })

      // Bessere Fehlermeldungen für verschiedene Fehlertypen
      let errorMessage = 'Unbekannter Fehler'
      
      // Firebase Functions Fehler
      if (error.code === 'functions/not-found') {
        errorMessage = 'Cloud Function nicht gefunden. Bitte kontaktieren Sie den Administrator.'
      } else if (error.code === 'functions/internal' || error.code === 'internal') {
        errorMessage = error.message || 'Interner Serverfehler. Bitte versuchen Sie es erneut.'
      } else if (error.code === 'deadline-exceeded' || error.message?.includes('Timeout')) {
        errorMessage = 'Export hat zu lange gedauert. Bitte versuchen Sie es erneut oder reduzieren Sie die Anzahl der Bilder.'
      } else if (error.code === 'resource-exhausted' || error.message?.includes('zu groß')) {
        errorMessage = error.message || 'Export-Datei ist zu groß. Bitte reduzieren Sie die Anzahl der Bilder.'
      } else if (error.code === 'unauthenticated' || error.code === 'functions/unauthenticated') {
        errorMessage = 'Sie sind nicht angemeldet. Bitte melden Sie sich an und versuchen Sie es erneut.'
      } else if (error.code === 'not-found' || error.code === 'functions/not-found') {
        errorMessage = 'Wissensbeitrag nicht gefunden. Bitte laden Sie die Seite neu.'
      } else if (error.code === 'invalid-argument' || error.code === 'functions/invalid-argument') {
        errorMessage = 'Ungültige Eingabedaten. Bitte laden Sie die Seite neu.'
      } else if (error.message) {
        errorMessage = error.message
      } else {
        errorMessage = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
      }

      toast.error(`Fehler beim Exportieren: ${errorMessage}`, {
        id: loadingToast,
        duration: 6000
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleTouchStart = e => {
    const touchStartY = e.touches[0].clientY
    const touchStartTime = Date.now()
    e.currentTarget.dataset.touchStartY = touchStartY
    e.currentTarget.dataset.touchStartTime = touchStartTime
    e.currentTarget.dataset.touchStartScrollTop = e.currentTarget.scrollTop
  }

  const handleTouchEnd = e => {
    const touchStartY = parseInt(e.currentTarget.dataset.touchStartY || 0)
    const touchStartTime = parseInt(e.currentTarget.dataset.touchStartTime || 0)
    const touchStartScrollTop = parseInt(e.currentTarget.dataset.touchStartScrollTop || 0)
    const touchEndY = e.changedTouches[0].clientY
    const touchEndTime = Date.now()
    const swipeDistance = touchEndY - touchStartY
    const swipeDuration = touchEndTime - touchStartTime
    const currentScrollTop = e.currentTarget.scrollTop

    // Prüfe ob wirklich am Top des Scrolls (mit Toleranz von 5px)
    const isAtTop = currentScrollTop <= 5 && touchStartScrollTop <= 5
    
    // Prüfe ob der Swipe nach unten war und ausreichend langsam (nicht versehentlich beim Scrollen)
    const isDownwardSwipe = swipeDistance > 0
    const isSlowSwipe = swipeDuration > 200 // Mindestens 200ms für einen bewussten Swipe
    
    // Swipe down to close (nur wenn am Top, bewusster Swipe nach unten, und mindestens 200px)
    if (isAtTop && isDownwardSwipe && isSlowSwipe && swipeDistance > 200) {
      onClose()
    }
  }

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <>
      {isExporting && <LoadingSpinner />}
      <div className='modal-overlay' onClick={onClose}>
        <div
          className='modal-content large'
          onClick={e => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={modalHeight ? { height: modalHeight, maxHeight: modalHeight } : {}}
        >
        <div className='modal-header'>
          <div>
            <span className='category-icon-large'>
              {getCategoryIcon(item.category)}
            </span>
            <h2 style={{ margin: '0' }}>{item.problemTitle}</h2>
          </div>
          <div className='modal-header-actions'>
            <button
              className='export-btn'
              onClick={handleExport}
              disabled={isExporting}
              title='Als PDF mit Ressourcen exportieren'
            >
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='7 10 12 15 17 10' />
                <line x1='12' y1='15' x2='12' y2='3' />
              </svg>
              {isExporting ? 'Exportiere...' : 'Exportieren'}
            </button>
            <button className='close-btn' onClick={onClose}>
              <svg
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <line x1='18' y1='6' x2='6' y2='18' />
                <line x1='6' y1='6' x2='18' y2='18' />
              </svg>
            </button>
          </div>
        </div>

        <div className='modal-body'>
          <div className='detail-header'>
            <span className='device-name-large'>{item.deviceDisplayName || item.deviceName}</span>
            {item.errorCode && (
              <span className='error-code-large'>{item.errorCode}</span>
            )}
            <span
              className='difficulty-badge-large'
              style={{
                background: getDifficultyColor(item.difficulty)
              }}
            >
              {getDifficultyLabel(item.difficulty)}
            </span>
            <span className='category-badge-large'>
              {getCategoryLabel(item.category)}
            </span>
          </div>

          {item.problemDescription && (
            <div className='detail-section'>
              <h4>Problem</h4>
              <p>{item.problemDescription}</p>
            </div>
          )}

          <div className='detail-section'>
            <h4>Lösung</h4>
            <div className='solution-content'>
              <SolutionWithImageReferences
                solution={item.enhancedSolution || item.solution}
                images={item.images || []}
              />
            </div>
          </div>

          {item.images && item.images.length > 0 && (
            <div className='detail-section'>
              <h4>Resourcen ({item.images.length})</h4>
              <ImageGallery images={item.images} compact={false} />
            </div>
          )}

          {item.components && item.components.length > 0 && (
            <div className='detail-section'>
              <h4>Betroffene Komponenten</h4>
              <div className='component-tags'>
                {item.components.map((comp, idx) => (
                  <span key={idx} className='component-tag'>
                    {comp}
                  </span>
                ))}
              </div>
            </div>
          )}

          {item.toolsRequired && item.toolsRequired.length > 0 && (
            <div className='detail-section'>
              <h4>Benötigte Werkzeuge</h4>
              <div className='tool-tags'>
                {item.toolsRequired.map((tool, idx) => (
                  <span key={idx} className='tool-tag'>
                    <svg
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                    >
                      <path d='M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z' />
                    </svg>
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className='detail-footer'>
            <div className='meta-info'>
              <span>
                Eingereicht von: <strong>{item.submittedBy}</strong>
              </span>
              <span>
                Freigegeben von: <strong>{item.moderatedBy}</strong>
              </span>
              <span>
                Datum:{' '}
                <strong>
                  {item.moderatedAt?.toDate
                    ? new Date(item.moderatedAt.toDate()).toLocaleDateString(
                        'de-DE'
                      )
                    : 'Unbekannt'}
                </strong>
              </span>
              {item.estimatedTime && (
                <span>
                  Dauer: <strong>{item.estimatedTime}</strong>
                </span>
              )}
            </div>
          </div>

          {/* Interactions Component */}
          <KnowledgeInteractions
            knowledgeId={item.id}
            currentUser={currentUser}
            userRole={userRole}
          />

          {/* History Component (Moderators only) */}
          <KnowledgeHistory
            knowledgeId={item.id}
            currentUser={currentUser}
            userRole={userRole}
          />
        </div>
      </div>

      {/* Export Settings Modal */}
      <ExportSettingsModal
        isOpen={showExportSettings}
        onClose={() => setShowExportSettings(false)}
        onConfirm={performExport}
        selectedLanguage='de'
      />
      </div>
    </>
  )
}

export default KnowledgeDetailModal
